import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LayoutDashboard,
  Calendar,
  PenTool,
  CheckSquare,
  Send,
  BarChart3,
  Settings,
  Sparkles,
  Menu,
  X,
  ChevronRight,
  Mail,
  LogOut,
  User,
  FileText,
  Megaphone
} from "lucide-react";

// Pages
import Dashboard from "./pages/Dashboard";
import CalendarPage from "./pages/CalendarPage";
import ContentCreator from "./pages/ContentCreator";
import ValidationQueue from "./pages/ValidationQueue";
import Publications from "./pages/Publications";
import Analytics from "./pages/Analytics";
import SettingsPage from "./pages/SettingsPage";
import EmailsPage from "./pages/EmailsPage";
import BrandSelector from "./components/BrandSelector";
import DocumentsPage from "./pages/DocumentsPage";
import CampaignsPage from "./pages/CampaignsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Components
import Chatbot from "./components/Chatbot";
import LouisAvatar from "./components/LouisAvatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Navigation items
const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Tableau de bord" },
  { path: "/calendar", icon: Calendar, label: "Calendrier" },
  { path: "/create", icon: PenTool, label: "Créer du contenu" },
  { path: "/validation", icon: CheckSquare, label: "Validation" },
  { path: "/publications", icon: Send, label: "Publications" },
  { path: "/emails", icon: Mail, label: "Emails" },
  { path: "/campaigns", icon: Megaphone, label: "Campagnes" },
  { path: "/documents", icon: FileText, label: "Documents" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/settings", icon: Settings, label: "Paramètres" },
];

// Configure axios with token
const setupAxiosInterceptors = (token) => {
  axios.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : '';
};

// Sidebar Component
const Sidebar = ({ isOpen, onClose, user, onLogout }) => {
  const location = useLocation();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      <aside className={`sidebar ${isOpen ? 'open' : ''}`} style={{ zIndex: 50 }} data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <LouisAvatar size={40} />
            <div>
              <h1 className="font-bold text-lg tracking-tight">CLYDE</h1>
              <p className="text-xs text-muted-foreground">Agent IA Marketing</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `nav-item relative ${isActive ? 'active' : ''}`
              }
              onClick={onClose}
              data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info & Logout */}
        <div className="p-4 border-t border-border space-y-3">
          {/* User */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Utilisateur'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>

          {/* Brand selector */}
          <BrandSelector onBrandChange={(brand) => console.log('Brand changed:', brand.name)} />

          {/* Logout */}
          <button
            onClick={onLogout}
            className="flex items-center gap-2 w-full p-3 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// Layout wrapper
const Layout = ({ children, user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Get page title
  const getPageTitle = () => {
    const item = navItems.find(item => item.path === location.pathname);
    return item?.label || "CLYDE";
  };

  return (
    <div className="app-layout">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        user={user}
        onLogout={onLogout}
      />
      
      <main className="main-content">
        {/* Header */}
        <header className="page-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors relative z-[60]"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-toggle"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <h1 className="text-2xl font-bold">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Bonjour, {user?.name?.split(' ')[0] || 'Utilisateur'}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="page-content">
          {children}
        </div>
      </main>
    </div>
  );
};

// Protected Route component
const ProtectedRoute = ({ children, user }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem("clyde_token");
    const savedUser = localStorage.getItem("clyde_user");
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setToken(savedToken);
        setupAxiosInterceptors(savedToken);
      } catch (e) {
        localStorage.removeItem("clyde_token");
        localStorage.removeItem("clyde_user");
      }
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    // Seed initial data when user is logged in
    const initializeApp = async () => {
      if (user && token) {
        try {
          await axios.post(`${API}/seed`);
          setInitialized(true);
        } catch (error) {
          console.error("Init error:", error);
          setInitialized(true);
        }
      }
    };

    if (user) {
      initializeApp();
    }
  }, [user, token]);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    setupAxiosInterceptors(userToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("clyde_token");
    localStorage.removeItem("clyde_user");
    setUser(null);
    setToken(null);
    setupAxiosInterceptors(null);
    toast.success("Déconnexion réussie");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LouisAvatar size={64} />
          <p className="text-muted-foreground">Chargement de CLYDE...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster 
        theme="light" 
        position="top-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--foreground))'
          }
        }}
      />
      
      {/* Chatbot only for logged in users */}
      {user && <Chatbot />}
      
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={
          user ? <Navigate to="/" replace /> : <LoginPage onLogin={handleLogin} />
        } />
        <Route path="/register" element={
          user ? <Navigate to="/" replace /> : <RegisterPage onLogin={handleLogin} />
        } />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><Dashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><CalendarPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/create" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><ContentCreator /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/validation" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><ValidationQueue /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/publications" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><Publications /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/emails" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><EmailsPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/campaigns" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><CampaignsPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><DocumentsPage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><Analytics /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}><SettingsPage /></Layout>
          </ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
